import { AbsoluteModelPath, createAbsoluteFromMask, PathTokens } from "../../../Common/ModelPath/ModelPath";
import { MultiLineFilters } from "../Controls/MultiControls/MultiLine/MultiLineFilter";
import { BindingPath } from "../Engine/Core/CoreTypes";
import { IViewModelContainer } from "../Engine/Core/Store/FullFormState";
import { ModelPathBuilder } from "../../../Common/ModelPath/ModelPathBuilder";
import { MatchKind } from "../../../Common/ModelPath/PathMatcher";
import { AbsoluteModelFieldPath } from "../../../Common/ModelPath/AbsoluteModelFieldPath";
import { IterUtils } from "../../../Common/IterableUtils";
import { nextTick } from "../../../Common/AsyncUtils";

import { compileFilterExpression } from "./FilterExpressions/CompileFilterExpression";
import { FilterExpression } from "./FilterExpressions/FilterExpression";
import { getMultipleValues, resolveMask } from "./StateAccessors/MultilineHelpers";
import { ViewModelSelect } from "./StateAccessors/ViewModelSelect";

export async function filterInstancesBatchedAsync(
    viewModel: IViewModelContainer,
    viewModelSelect: ViewModelSelect,
    filterExpressions: FilterExpression[],
    multilinePath: BindingPath,
    batchSize = 100,
    abortSignal?: AbortSignal
): Promise<string[]> {
    const instances = viewModelSelect.children(viewModel, multilinePath);
    const filterConditionFunctions = filterExpressions.map(compileFilterExpression);

    const filteredInstances: string[] = [];

    for (const [index, instance] of IterUtils.iterateEntries(instances)) {
        const getInstanceValueFn = (dataPath: BindingPath) => {
            const resolvedFilterPathToMultilinePath = resolveFilterDataPath(
                dataPath,
                createAbsoluteFromMask(multilinePath, PathTokens.each)
            );
            const resolvedPath = resolveMask(resolvedFilterPathToMultilinePath.toString(), instance);
            const modelFieldPath = AbsoluteModelFieldPath.createFromMask(resolvedPath, PathTokens.each);
            return getMultipleValues(viewModel, modelFieldPath.path, modelFieldPath.field);
        };
        const getInstanceFn = () => instance;
        const isMatch = filterConditionFunctions.every(fn => fn(getInstanceValueFn, getInstanceFn));
        if (isMatch) {
            filteredInstances.push(instance);
        }
        if (index % batchSize === 0) {
            await nextTick();
            if (abortSignal?.aborted) {
                throw new DOMException("Operation cancelled", "AbortError");
            }
        }
    }

    return filteredInstances;
}

export function isNotEmptyFilters(filters: MultiLineFilters | undefined): boolean {
    return (filters?.length ?? 0) > 0;
}

export function getFilterKey(dataPaths: readonly AbsoluteModelPath[]): string {
    return dataPaths.map(x => x.toLegacyPath()).join("_");
}

export function resolveFilterDataPath(filterPath: string, multiplicityPath: AbsoluteModelPath): AbsoluteModelFieldPath {
    const resultPath = new ModelPathBuilder(false);
    const modelFieldPathFromFilterPath = AbsoluteModelFieldPath.createFromMask(filterPath, PathTokens.each);
    const path = modelFieldPathFromFilterPath.path;
    const matchedPath = path.matchFor(multiplicityPath);
    for (const match of matchedPath) {
        if (match.kind === MatchKind.DifferentSuffix) {
            resultPath.append(match.source ?? []);
        } else if (match.kind === MatchKind.MatchedPath) {
            resultPath.append(match.prefix);
        } else if (match.kind === MatchKind.MatchedMultiplicity) {
            resultPath.append(match.target);
        }
    }
    return new AbsoluteModelFieldPath(resultPath.toAbsolutePath(), modelFieldPathFromFilterPath.field);
}
